import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { STATUS_CONFIG } from '../../data/mockData';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { StatusTracker } from '../../components/shared/StatusTracker';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useApp, ROLES } from '../../context/AppContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Upload,
  Download,
  MoreHorizontal,
  Edit,
  Trash2,
  Share2,
  MessageCircle,
  Copy,
  Eye,
  ZoomIn,
  ExternalLink,
  Check,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { fetchCases, saveGeotag, updateCaseAction, uploadCaseFile, logActivity } from '../../api';
import { generatePrintableCaseReport, generatePrintableDocument } from '../../utils/printReport';
import { CaseShareModal } from '../../components/shared/CaseShareModal';
import { CaseCallModal } from '../../components/shared/CaseCallModal';

const priorityColors = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentRole, currentUserId, currentUser, caseStatusOverrides, submitDeletionRequest } = useApp();
  const isClient = currentRole === ROLES.CLIENT;
  const [activeTab, setActiveTab] = useState('details');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLiveCases = async () => {
    try {
      const data = await fetchCases();
      setCases(data);
    } catch (err) {
      console.warn("Failed to load live cases from backend", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLiveCases();
  }, [id]);

  // Match by id or refNumber case-insensitively, or fallback
  const normalizedId = String(id || '').toLowerCase().trim();
  const caseData = cases.find(c =>
    String(c.id || '').toLowerCase().trim() === normalizedId ||
    String(c.refNumber || '').toLowerCase().trim() === normalizedId
  ) || null;

  // Merge status override
  const caseStatus = caseStatusOverrides[id] || caseData?.status || 'Initiated';
  const statusStep = STATUS_CONFIG[caseStatus]?.step || 1;

  // Deletion modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteRemark, setDeleteRemark] = useState('');
  const [attachedFileName, setAttachedFileName] = useState('');

  const resolveImgUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return url;
    return `/${url}`;
  };

  // Hydrate images from live database verifiedImages if they exist
  const liveImages = (Array.isArray(caseData?.verifiedImages) && caseData.verifiedImages.length > 0)
    ? caseData.verifiedImages.map((img, index) => ({
      id: img.id || index + 1,
      url: resolveImgUrl(img.url),
      tags: img.latitude ? {
        dateTime: img.timestamp || new Date().toISOString().slice(0, 16),
        latLng: `${img.latitude}, ${img.longitude}`,
        locationName: img.address || ''
      } : null
    }))
    : [];

  const [images, setImages] = useState([]);
  const docInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [documents, setDocuments] = useState([
    { name: 'Aadhaar Card.pdf', size: '256 KB', type: 'pdf' },
    { name: 'Electricity Bill.pdf', size: '512 KB', type: 'pdf' },
    { name: 'PAN Card.pdf', size: '180 KB', type: 'pdf' },
  ]);

  const [editedCase, setEditedCase] = useState({
    applicantName: '',
    applicantPhone: '',
    applicantEmail: '',
    address: '',
    priority: 'high',
  });

  useEffect(() => {
    setImages(liveImages);
    if (Array.isArray(caseData?.documents) && caseData.documents.length > 0) {
      setDocuments(caseData.documents);
    }
    setEditedCase({
      applicantName: caseData?.applicantName || '',
      applicantPhone: caseData?.applicantPhone || '',
      applicantEmail: caseData?.applicantEmail || '',
      address: caseData?.address || '',
      priority: (caseData?.priority || 'medium').toLowerCase(),
    });
  }, [cases, id, caseData]);

  const handleDocumentClick = (doc) => {
    generatePrintableDocument(doc, caseData);
    toast.success(`Opening verification document: ${doc.name}`);
    logActivity({
      action: 'Document Viewed',
      description: `${currentUser?.name || 'User'} viewed/printed verification document ${doc.name} for ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  const handleUploadDocument = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const newDoc = {
      name: file.name,
      size: `${Math.max(1, Math.round(file.size / 1024))} KB`,
      type: file.type || 'pdf'
    };

    setDocuments(prev => [newDoc, ...prev]);
    toast.success(`Document "${file.name}" uploaded successfully!`);
    logActivity({
      action: 'Document Uploaded',
      description: `${currentUser?.name || 'User'} uploaded document ${file.name} to case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
    e.target.value = '';
  };

  const handleUploadImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImgs = files.map((file, idx) => ({
      id: Date.now() + idx,
      url: URL.createObjectURL(file),
      tags: {
        dateTime: new Date().toISOString().slice(0, 16).replace('T', ' '),
        latLng: caseData.latitude && caseData.longitude ? `${caseData.latitude}, ${caseData.longitude}` : '28.5355, 77.3910',
        locationName: caseData.address || 'Noida, UP'
      }
    }));

    setImages(prev => [...newImgs, ...prev]);
    toast.success(`${files.length} verification image(s) uploaded successfully!`);
    logActivity({
      action: 'Images Uploaded',
      description: `${currentUser?.name || 'User'} uploaded ${files.length} photos for case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
    e.target.value = '';
  };

  const handleDeleteImage = (imgId) => {
    setImages(prev => prev.filter(img => img.id !== imgId));
    toast.success('Image removed from verification dossier');
  };

  const downloadImage = (img) => {
    const a = document.createElement('a');
    a.href = img.url;
    a.download = `Verification_Photo_${img.id || Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Photo download initiated');
  };

  const handleSaveEdit = () => {
    if (!editedCase.applicantName.trim()) {
      toast.error('Applicant name cannot be empty');
      return;
    }
    caseData.applicantName = editedCase.applicantName;
    caseData.applicantPhone = editedCase.applicantPhone;
    caseData.applicantEmail = editedCase.applicantEmail;
    caseData.address = editedCase.address;
    caseData.priority = editedCase.priority;
    setShowEditModal(false);
    toast.success('Case details updated successfully!');
    logActivity({
      action: 'Case Updated',
      description: `${currentUser?.name || 'User'} updated applicant profile for case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  // Geo Tagging State
  const [taggingImage, setTaggingImage] = useState(null);
  const [tagData, setTagData] = useState({ dateTime: '', latLng: '', locationName: '' });

  // Mock Call Logs outreach data
  const mockCallLogs = [
    {
      id: 'CALL001',
      agent: 'Vikram Singh',
      phone: caseData.applicantPhone || '+91 99887 76655',
      duration: '2m 15s',
      status: 'Connected',
      timestamp: '2026-05-24T10:15:00Z',
      type: 'Pre-Verification Check',
      recordingUrl: 'https://actions.google.com/sounds/v1/ambient/morning_birds.ogg',
      summary: 'Verified applicant name and residence landmark. Confirmed availability for physical verification tomorrow at 4 PM.'
    },
    {
      id: 'CALL002',
      agent: 'Vikram Singh',
      phone: caseData.applicantPhone || '+91 99887 76655',
      duration: '0s',
      status: 'No Answer',
      timestamp: '2026-05-24T10:02:00Z',
      type: 'Verification Coordinator Call',
      recordingUrl: null,
      summary: 'Outbound call made to confirm address details. Applicant did not answer.'
    },
    {
      id: 'CALL003',
      agent: 'System Auto-Dialer',
      phone: caseData.applicantPhone || '+91 99887 76655',
      duration: '45s',
      status: 'Connected',
      timestamp: '2026-05-23T14:30:00Z',
      type: 'Welcome Call / Welcome IVR',
      recordingUrl: 'https://actions.google.com/sounds/v1/ambient/morning_birds.ogg',
      summary: 'Auto-triggered welcome IVR call successful. Consent captured for location sharing.'
    },
  ];

  const [playingCallId, setPlayingCallId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);

  // Simulating call recording playback progress
  useEffect(() => {
    let interval;
    if (isPlaying && playingCallId) {
      interval = setInterval(() => {
        setPlayProgress((prev) => {
          if (prev >= 100) {
            setIsPlaying(false);
            setPlayingCallId(null);
            return 0;
          }
          return prev + 5;
        });
      }, 500);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPlaying, playingCallId]);

  // Boundary check
  if (isClient && caseData && caseData.client_id !== 'CLT001' && caseData.client?.id !== 'CLT001') {
    return (
      <div className="crm-card p-6 text-center text-red-600 font-semibold max-w-md mx-auto mt-20">
        Access Denied. You do not have permission to view this case details.
      </div>
    );
  }

  const canGeoTag = [
    ROLES.ADMIN,
    ROLES.SUPREME_MANAGER,
    ROLES.MANAGER,
    ROLES.QUALITY_CHECKER,
    ROLES.UNIVERSAL_QC
  ].includes(currentRole);

  const saveGeoTag = async () => {
    try {
      let lat = null;
      let lng = null;
      if (tagData.latLng) {
        const parts = tagData.latLng.split(',');
        lat = parseFloat(parts[0]) || null;
        lng = parseFloat(parts[1]) || null;
      }

      await saveGeotag(caseData.id, taggingImage.id, lat, lng, tagData.locationName);
      loadLiveCases();
    } catch (err) {
      console.error("Failed to save geo tag via API", err);
    }

    setImages(prev => prev.map(img => img.id === taggingImage.id ? { ...img, tags: tagData } : img));
    setTaggingImage(null);
    setTagData({ dateTime: '', latLng: '', locationName: '' });
  };

  const handleWhatsApp = () => {
    if (!caseData) return;
    const origin = window.location.origin;
    const caseUrl = `${origin}/cases/${caseData.id}`;
    const cleanPhone = (caseData.applicantPhone || '').replace(/[^0-9]/g, '');
    const clientName = caseData.client?.name || caseData.clientName || 'N/A';
    const productName = caseData.product?.name || caseData.productName || 'Address Verification';
    const msg = `🏢 *AD VENTURES - CASE VERIFICATION*\n━━━━━━━━━━━━━━━━━━━━\n📋 *Case Ref:* ${caseData.refNumber}\n👤 *Applicant:* ${caseData.applicantName}\n📞 *Phone:* ${caseData.applicantPhone || 'N/A'}\n📍 *Address:* ${caseData.address || 'N/A'}\n🏛️ *Client:* ${clientName}\n🔍 *Verification:* ${productName}\n📊 *Status:* ${(caseData.status || 'Active').toUpperCase()}\n\n🔗 *View Case Online:*\n${caseUrl}`;

    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(msg)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;

    window.open(waUrl, '_blank', 'noopener,noreferrer');
    toast.success(`WhatsApp share opened for ${caseData.refNumber}!`);

    logActivity({
      action: 'WhatsApp Shared',
      description: `${currentUser?.name || 'User'} shared case ${caseData.refNumber} via WhatsApp to ${caseData.applicantName} (${caseData.applicantPhone || 'Applicant'})`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  const handleEmail = () => {
    if (!caseData) return;
    const origin = window.location.origin;
    const caseUrl = `${origin}/cases/${caseData.id}`;
    const subject = `Case Verification Details - ${caseData.refNumber} (${caseData.applicantName})`;
    const body = `Dear ${caseData.applicantName || 'Applicant'},\n\nHere are the details for Case Reference: ${caseData.refNumber}\n\nApplicant: ${caseData.applicantName}\nAddress: ${caseData.address || 'N/A'}\nClient: ${caseData.client?.name || 'N/A'}\nStatus: ${caseData.status}\n\nView case online:\n${caseUrl}\n\nBest regards,\nAd Ventures`;

    window.location.href = `mailto:${caseData.applicantEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success(`Email client opened for ${caseData.refNumber}`);

    logActivity({
      action: 'Email Shared',
      description: `${currentUser?.name || 'User'} initiated email for case ${caseData.refNumber} to ${caseData.applicantEmail || 'Applicant'}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  const handlePdf = () => {
    if (!caseData) return;
    generatePrintableCaseReport(caseData);
    toast.success(`PDF report generated for ${caseData.refNumber}!`);

    logActivity({
      action: 'PDF Report Generated',
      description: `${currentUser?.name || 'User'} generated and downloaded PDF report for case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  const handleViewMap = () => {
    if (!caseData) return;
    const query = caseData.latitude && caseData.longitude
      ? `${caseData.latitude},${caseData.longitude}`
      : encodeURIComponent(caseData.address || 'India');
    window.open(`https://maps.google.com/?q=${query}`, '_blank');
  };

  if (!loading && !caseData) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm max-w-lg mx-auto my-12">
        <h3 className="text-lg font-bold text-slate-800">Case Not Found</h3>
        <p className="text-sm text-slate-500 mt-2">The case ID or reference number you requested does not exist or has been deleted.</p>
        <Button onClick={() => navigate('/cases')} className="mt-4 bg-blue-600 text-white">Back to Cases</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/cases')}
            className="text-slate-500 hover:text-slate-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{caseData?.refNumber || id}</h1>
              <StatusBadge
                status={STATUS_CONFIG[caseStatus]?.color || 'neutral'}
                label={STATUS_CONFIG[caseStatus]?.label || caseStatus}
              />
              <Badge variant="outline" className={priorityColors[(caseData?.priority || 'medium').toLowerCase()] || priorityColors.medium}>
                {((caseData?.priority || 'medium').charAt(0).toUpperCase() + (caseData?.priority || 'medium').slice(1))} Priority
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {caseData?.product?.name || caseData?.productName || 'Address Verification'} • {caseData?.client?.name || caseData?.clientName || 'Client Partner'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="bg-white border-blue-200 text-blue-700 hover:bg-blue-50 gap-2 font-semibold"
            onClick={() => setShareModalOpen(true)}
          >
            <Share2 className="w-4 h-4 text-blue-600" />
            Share Case
          </Button>
          {!isClient && (
            <Button
              variant="outline"
              className="bg-white border-slate-200 gap-2 hover:bg-slate-50 font-semibold text-slate-700"
              onClick={() => setShowEditModal(true)}
              data-testid="edit-case-btn"
            >
              <Edit className="w-4 h-4 text-slate-600" />
              Edit
            </Button>
          )}
          <Button
            variant="outline"
            className="bg-white border-slate-200 gap-2"
            onClick={handlePdf}
            data-testid="download-report-btn"
          >
            <Download className="w-4 h-4" />
            Download Report
          </Button>
          {isClient && (
            <Button
              className="bg-red-600 hover:bg-red-700 text-white gap-2 shadow-lg shadow-red-500/10"
              onClick={() => setShowDeleteModal(true)}
              data-testid="request-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
              Request Delete
            </Button>
          )}
        </div>
      </div>

      {/* Status Tracker */}
      <div className="crm-card p-6">
        <h3 className="text-sm font-medium text-slate-500 mb-4">Case Progress</h3>
        <StatusTracker currentStep={statusStep - 1} />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Details */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger value="details" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                Details
              </TabsTrigger>
              <TabsTrigger value="verification" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                Verification Form
              </TabsTrigger>
              <TabsTrigger value="images" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                Images ({images.length})
              </TabsTrigger>
              {![ROLES.CLIENT, ROLES.BUSINESS_PARTNER, ROLES.VENDOR].includes(currentRole) && (
                <TabsTrigger value="qc" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                  QC Logs
                </TabsTrigger>
              )}
              <TabsTrigger value="calls" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                Call Logs
              </TabsTrigger>
              <TabsTrigger value="timeline" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
                Timeline
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="mt-6">
              <div className="crm-card p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Applicant Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{caseData.applicantName}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{caseData.applicantPhone}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{caseData.applicantEmail}</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                        <span className="text-sm text-slate-900">{caseData.address}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Case Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Created</span>
                        <span className="text-sm text-slate-900">
                          {(() => {
                            try { return caseData?.createdAt ? format(new Date(caseData.createdAt), 'MMM dd, yyyy hh:mm a') : 'N/A'; }
                            catch { return 'N/A'; }
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Due Date</span>
                        <span className="text-sm text-slate-900">
                          {(() => {
                            try { return caseData?.dueDate ? format(new Date(caseData.dueDate), 'MMM dd, yyyy') : 'N/A'; }
                            catch { return 'N/A'; }
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Last Updated</span>
                        <span className="text-sm text-slate-900">
                          {(() => {
                            try { return caseData?.updatedAt ? format(new Date(caseData.updatedAt), 'MMM dd, yyyy hh:mm a') : 'N/A'; }
                            catch { return 'N/A'; }
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Documents */}
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Verification Documents</h4>
                      <p className="text-xs text-slate-400">Click any document to inspect, watermark or print</p>
                    </div>
                    <input
                      type="file"
                      ref={docInputRef}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={handleUploadDocument}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white border-slate-200 gap-2 text-slate-700 hover:bg-slate-50 font-medium"
                      onClick={() => docInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 text-blue-600" />
                      Upload Document
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {documents.length > 0 ? (
                      documents.map((doc, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 cursor-pointer transition-all group shadow-2xs"
                          onClick={() => handleDocumentClick(doc)}
                          title="Click to view and print document"
                        >
                          <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-900 truncate group-hover:text-blue-600">{doc.name || 'Document'}</p>
                            <p className="text-xs text-slate-500">{doc.size || 'PDF'} • Audit Ready</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 text-slate-400 hover:text-blue-700 hover:bg-blue-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentClick(doc);
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 col-span-2 py-4 text-center">No documents uploaded yet</p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Verification Form Tab */}
            <TabsContent value="verification" className="mt-6">
              <div className="crm-card p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="residenceType">Residence Type</Label>
                    <Input id="residenceType" value="Owned" readOnly className="bg-slate-50/50 border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="localityType">Locality Type</Label>
                    <Input id="localityType" value="Urban" readOnly className="bg-slate-50/50 border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="landmark">Landmark</Label>
                    <Input id="landmark" value="Near City Mall" readOnly className="bg-slate-50/50 border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="yearsAtAddress">Years at Address</Label>
                    <Input id="yearsAtAddress" value="5 years" readOnly className="bg-slate-50/50 border-slate-200" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="remarks">Verifier Remarks</Label>
                  <Textarea id="remarks" value="Address verified successfully. Property matches the documents provided." readOnly className="bg-slate-50/50 border-slate-200" rows={4} />
                </div>
              </div>
            </TabsContent>

            {/* Images Tab */}
            <TabsContent value="images" className="mt-6">
              <div className="crm-card p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-slate-800">Uploaded Verification Photos ({images.length}/20)</h4>
                    <p className="text-xs text-slate-400">Click any image to enlarge, inspect GPS watermark, or download</p>
                  </div>
                  <input
                    type="file"
                    ref={imageInputRef}
                    className="hidden"
                    multiple
                    accept="image/*"
                    onChange={handleUploadImages}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-white border-slate-200 gap-2 text-slate-700 hover:bg-slate-50 font-semibold"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 text-blue-600" />
                    Upload Photos
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((img) => (
                    <div
                      key={img.id}
                      className="relative aspect-square rounded-xl overflow-hidden group bg-slate-100 border border-slate-200 cursor-pointer shadow-2xs hover:shadow-md transition-all"
                      onClick={() => setPreviewImage(img)}
                    >
                      <img src={img.url} alt={`Verification ${img.id}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                      {/* Geo Tag Display (if exists) */}
                      {img.tags && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white text-[10px] p-2 flex flex-col gap-0.5 backdrop-blur-xs">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-emerald-400" /> {img.tags.dateTime}</span>
                          <span className="flex items-center gap-1 font-mono text-[9px]"><MapPin className="w-3 h-3 text-red-400" /> {img.tags.latLng}</span>
                          <span className="truncate flex items-center gap-1"><MapPin className="w-3 h-3 text-blue-400" /> {img.tags.locationName}</span>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        <Button
                          size="sm"
                          className="bg-white text-slate-900 hover:bg-slate-100 gap-1 text-xs font-bold shadow-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(img);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5 text-blue-600" /> Inspect & Zoom
                        </Button>
                        <div className="flex items-center gap-2">
                          {canGeoTag && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white border-none text-xs gap-1 h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTaggingImage(img);
                                if (img.tags) setTagData(img.tags);
                              }}
                            >
                              <MapPin className="w-3 h-3" /> Geo Tag
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="icon"
                            className="w-7 h-7 rounded-full shadow"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(img.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* QC Logs Tab */}
            {![ROLES.CLIENT, ROLES.BUSINESS_PARTNER].includes(currentRole) && (
              <TabsContent value="qc" className="mt-6">
                <div className="crm-card p-6 space-y-4">
                  {Array.isArray(caseData?.qcLogs) && caseData.qcLogs.length > 0 ? (
                    caseData.qcLogs.map((log, index) => (
                      <div key={index} className="flex gap-4 p-4 rounded-lg bg-white">
                        <div className={`w-10 h-10 rounded-full border flex items-center justify-center ${log.action === 'Approved' ? 'bg-emerald-50 border-emerald-100' :
                            log.action === 'Rejected' ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'
                          }`}>
                          {log.action === 'Approved' ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : log.action === 'Rejected' ? (
                            <XCircle className="w-5 h-5 text-red-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900">{log.action || 'QC Review'}</p>
                            <span className="text-xs text-slate-500">
                              {(() => {
                                try { return log.timestamp ? format(new Date(log.timestamp), 'MMM dd, yyyy hh:mm a') : 'Recent'; }
                                catch { return 'Recent'; }
                              })()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{log.comment || 'No comment provided'}</p>
                          <p className="text-xs text-slate-500 mt-2">by {log.user?.name || log.userName || 'QC Reviewer'}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No QC logs available</p>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Call Logs Tab */}
            <TabsContent value="calls" className="mt-6">
              <div className="crm-card p-6 space-y-6 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Applicant Outreach History</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Communication logs and voice call recordings with {caseData.applicantName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">
                      Telephony Sync Active
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {mockCallLogs.map((call) => {
                    const isCurrentPlaying = playingCallId === call.id && isPlaying;
                    return (
                      <div key={call.id} className="flex gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                        <div className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 ${call.status === 'Connected' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-rose-50 border-rose-100 text-rose-600'
                          }`}>
                          <Phone className="w-4 h-4" />
                        </div>

                        <div className="flex-1 space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{call.type}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Dialed <span className="font-semibold text-slate-700">{call.phone}</span> by {call.agent}
                              </p>
                            </div>
                            <div className="text-left sm:text-right">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${call.status === 'Connected' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                                }`}>
                                {call.status}
                              </span>
                              <p className="text-[10px] text-slate-400 mt-1">
                                {format(new Date(call.timestamp), 'MMM dd, yyyy hh:mm a')}
                              </p>
                            </div>
                          </div>

                          <div className="bg-white border border-slate-100 rounded-lg p-3 text-xs text-slate-600 leading-relaxed shadow-sm">
                            <span className="font-semibold text-slate-700">Summary: </span>
                            {call.summary}
                          </div>

                          {call.recordingUrl && (
                            <div className="flex items-center gap-3 bg-white border border-slate-100 rounded-lg p-3 shadow-sm">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 rounded-full p-0 flex items-center justify-center shrink-0 bg-blue-50 border-blue-100 hover:bg-blue-100 text-blue-600"
                                onClick={() => {
                                  if (playingCallId === call.id) {
                                    setIsPlaying(!isPlaying);
                                  } else {
                                    setPlayingCallId(call.id);
                                    setIsPlaying(true);
                                    setPlayProgress(0);
                                  }
                                }}
                              >
                                {isCurrentPlaying ? (
                                  <span className="flex items-center gap-0.5 justify-center">
                                    <span className="w-1 h-3 bg-blue-600 rounded-full animate-bounce" />
                                    <span className="w-1 h-3 bg-blue-600 rounded-full animate-bounce [animation-delay:0.15s]" />
                                  </span>
                                ) : (
                                  <span className="border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[6px] border-l-blue-600 ml-0.5" />
                                )}
                              </Button>

                              <div className="flex-1 space-y-1">
                                <div className="flex items-center justify-between text-[10px] text-slate-400">
                                  <span>{isCurrentPlaying ? 'Streaming Recording...' : 'Call Recording'}</span>
                                  <span>{call.duration}</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden relative">
                                  <div
                                    className={`h-full bg-blue-500 rounded-full transition-all duration-300 ${isCurrentPlaying ? 'animate-pulse' : ''}`}
                                    style={{ width: isCurrentPlaying ? `${playProgress}%` : '0%' }}
                                  />
                                </div>
                              </div>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-slate-700"
                                onClick={() => {
                                  toast.success(`Downloading recording_${call.id}.mp3...`);
                                }}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="mt-6">
              <div className="crm-card p-6">
                <ScrollArea className="h-80">
                  <div className="space-y-4">
                    {Array.isArray(caseData?.timeline) && caseData.timeline.length > 0 ? (
                      caseData.timeline.map((event, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="w-3 h-3 rounded-full bg-blue-600" />
                            {index < caseData.timeline.length - 1 && (
                              <div className="w-0.5 h-full bg-slate-200 my-1" />
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <p className="text-sm font-medium text-slate-900">{event.action || 'Activity'}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              by {event.user?.name || event.userName || 'System'} • {(() => {
                                try { return event.timestamp ? format(new Date(event.timestamp), 'MMM dd, yyyy hh:mm a') : 'Recent'; }
                                catch { return 'Recent'; }
                              })()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No timeline events recorded</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Panel - Sidebar */}
        <div className="space-y-6">
          {/* Assigned To */}
          <div className="crm-card p-6">
            <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Assigned To</h4>
            {caseData?.assignedTo ? (
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={caseData.assignedTo.avatar} />
                  <AvatarFallback>{(caseData.assignedTo.name || 'U').charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-slate-900">{caseData.assignedTo.name || 'Field Executive'}</p>
                  <p className="text-xs text-slate-500">{caseData.assignedTo.role || 'Field Executive'}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500">Not assigned</p>
                {!isClient && (
                  <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700">
                    Assign Now
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Client */}
          <div className="crm-card p-6">
            <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Client</h4>
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={caseData?.client?.logo} />
                <AvatarFallback>{(caseData?.client?.name || caseData?.clientName || 'C').charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-slate-900">{caseData?.client?.name || caseData?.clientName || 'Client Partner'}</p>
                <p className="text-xs text-slate-500">{caseData?.client?.contactPerson || 'Client Partner'}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="crm-card p-6">
            <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Quick Actions</h4>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-200 text-xs font-semibold"
                onClick={() => setCallModalOpen(true)}
              >
                <Phone className="w-4 h-4 text-teal-600" />
                Call Applicant & Log
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 text-xs font-semibold"
                onClick={handleWhatsApp}
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                Share on WhatsApp
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 text-xs font-semibold"
                onClick={handleEmail}
              >
                <Mail className="w-4 h-4 text-blue-600" />
                Send Email
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 text-xs font-semibold"
                onClick={() => setShareModalOpen(true)}
              >
                <Share2 className="w-4 h-4 text-indigo-600" />
                Share Case & Copy URL
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-xs font-semibold"
                onClick={handlePdf}
              >
                <FileText className="w-4 h-4 text-red-600" />
                Print / PDF Report
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start bg-white border-slate-200 gap-2 text-slate-700 hover:bg-slate-50 text-xs font-semibold"
                onClick={handleViewMap}
              >
                <MapPin className="w-4 h-4 text-slate-500" />
                View on Google Maps
              </Button>
              {isClient && (
                <Button
                  variant="destructive"
                  className="w-full justify-start gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 font-semibold"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                  Request Case Deletion
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Geo Tagging Dialog */}
      <Dialog open={!!taggingImage} onOpenChange={() => setTaggingImage(null)}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Manual Geo Tagging</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex justify-center mb-4">
              {taggingImage && (
                <img src={taggingImage.url} alt="To Tag" className="w-32 h-32 object-cover rounded-lg border border-slate-200" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={tagData.dateTime}
                onChange={e => setTagData({ ...tagData, dateTime: e.target.value })}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label>Latitude / Longitude</Label>
              <Input
                placeholder="e.g. 19.0760° N, 72.8777° E"
                value={tagData.latLng}
                onChange={e => setTagData({ ...tagData, latLng: e.target.value })}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label>Location Name</Label>
              <Input
                placeholder="e.g. Andheri East, Mumbai"
                value={tagData.locationName}
                onChange={e => setTagData({ ...tagData, locationName: e.target.value })}
                className="bg-slate-50 border-slate-200"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaggingImage(null)} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button onClick={saveGeoTag} className="bg-blue-600 hover:bg-blue-700">Save Tags</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Case Deletion Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Request Case Deletion
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Submit a formal request to delete this case. Deletion requires administrative review and approval.
            </DialogDescription>
          </DialogHeader>

          {caseData && (
            <div className="space-y-4 py-3">
              {/* Case Info Section */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Case ID:</span>
                  <span className="font-semibold text-slate-800">{caseData.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Applicant:</span>
                  <span className="font-semibold text-slate-800">{caseData.applicantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Product:</span>
                  <span className="font-semibold text-slate-800">{caseData?.product?.name || caseData?.productName || 'Address Verification'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Verification Type:</span>
                  <span className="font-semibold text-slate-800">{caseData?.verificationType || caseData?.product?.name || caseData?.productName || 'Address Verification'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Current Status:</span>
                  <span className="capitalize font-semibold text-slate-800">
                    {STATUS_CONFIG[caseStatus]?.label || caseStatus}
                  </span>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700">Delete Reason <span className="text-red-500">*</span></Label>
                  <Select value={deleteReason} onValueChange={setDeleteReason}>
                    <SelectTrigger className="bg-white border-slate-200 text-xs h-9">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="Duplicate Case">Duplicate Case</SelectItem>
                      <SelectItem value="Wrong Details">Wrong Details</SelectItem>
                      <SelectItem value="Client Request">Client Request</SelectItem>
                      <SelectItem value="Uploaded By Mistake">Uploaded By Mistake</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700">Remark / Explanation <span className="text-red-500">*</span></Label>
                  <textarea
                    className="w-full min-h-[70px] rounded-md border border-slate-200 p-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 bg-white"
                    placeholder="Provide detailed justification..."
                    value={deleteRemark}
                    onChange={(e) => setDeleteRemark(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Supporting Document <span className="text-slate-400 font-normal">(Optional)</span></Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      id="doc-upload"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setAttachedFileName(e.target.files[0].name);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-slate-200 hover:bg-slate-50 text-xs h-8 bg-transparent"
                      onClick={() => document.getElementById('doc-upload')?.click()}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Choose File
                    </Button>
                    {attachedFileName ? (
                      <span className="text-[11px] text-emerald-600 font-medium truncate max-w-[200px]">
                        {attachedFileName}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">
                        Supports PDF, JPG, PNG
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteReason('');
                setDeleteRemark('');
                setAttachedFileName('');
              }}
              className="border-slate-200 text-xs h-8 bg-transparent"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                if (!deleteReason || !deleteRemark) {
                  window.alert('Please select a reason and enter remarks.');
                  return;
                }

                try {
                  await updateCaseAction(caseData.id, 'delete_request', {
                    reason: deleteReason,
                    comment: deleteRemark
                  });
                  loadLiveCases();
                } catch (err) {
                  console.error("Failed to submit deletion request to backend database", err);
                }

                const request = {
                  id: `DEL${Date.now()}`,
                  caseId: caseData.id,
                  refNumber: caseData.refNumber,
                  applicantName: caseData.applicantName,
                  product: caseData?.product?.name || caseData?.productName || 'Address Verification',
                  verificationType: caseData?.verificationType || caseData?.product?.name || caseData?.productName || 'Address Verification',
                  currentStatus: caseData?.status || 'Active',
                  clientName: caseData?.client?.name || caseData?.clientName || 'Client Partner',
                  requestedBy: 'Neha Joshi (Client)',
                  reason: deleteReason,
                  remarks: deleteRemark,
                  attachmentName: attachedFileName || null,
                  requestDate: new Date().toISOString(),
                  status: 'pending',
                  adminRemark: ''
                };
                submitDeletionRequest(request);
                setShowDeleteModal(false);
                setDeleteReason('');
                setDeleteRemark('');
                setAttachedFileName('');
                window.alert('Case deletion request submitted successfully! Case status updated to Deletion Pending.');
              }}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8"
              disabled={!deleteReason || !deleteRemark}
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <CaseShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        caseData={caseData}
      />

      {/* Call Modal */}
      <CaseCallModal
        open={callModalOpen}
        onOpenChange={setCallModalOpen}
        caseData={caseData}
      />
      {/* Image Lightbox & Inspection Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                Verification Photo Preview
              </span>
            </DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden bg-slate-950 flex items-center justify-center max-h-[460px]">
                <img
                  src={previewImage.url}
                  alt="Full verification photo"
                  className="w-full h-auto max-h-[460px] object-contain"
                />
              </div>

              {previewImage.tags ? (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Capture Timestamp:</span>
                    <strong className="text-slate-800">{previewImage.tags.dateTime}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">GPS Coordinates:</span>
                    <strong className="font-mono text-blue-700">{previewImage.tags.latLng}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Physical Address:</span>
                    <strong className="text-slate-800">{previewImage.tags.locationName}</strong>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No GPS geotag metadata embedded for this capture.</p>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => setPreviewImage(null)}>
                  Close
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                  onClick={() => downloadImage(previewImage)}
                >
                  <Download className="w-4 h-4" /> Download Photo
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Case Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Case Profile - {caseData?.refNumber}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Update applicant details, residential address, contact number and priority level.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Applicant Full Name</Label>
              <Input
                value={editedCase.applicantName}
                onChange={(e) => setEditedCase(prev => ({ ...prev, applicantName: e.target.value }))}
                placeholder="Full Name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Phone Number</Label>
                <Input
                  value={editedCase.applicantPhone}
                  onChange={(e) => setEditedCase(prev => ({ ...prev, applicantPhone: e.target.value }))}
                  placeholder="+91 99887 76655"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Priority Level</Label>
                <Select
                  value={editedCase.priority}
                  onValueChange={(val) => setEditedCase(prev => ({ ...prev, priority: val }))}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="high">High Priority</SelectItem>
                    <SelectItem value="medium">Medium Priority</SelectItem>
                    <SelectItem value="low">Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Email Address</Label>
              <Input
                value={editedCase.applicantEmail}
                onChange={(e) => setEditedCase(prev => ({ ...prev, applicantEmail: e.target.value }))}
                placeholder="applicant@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Full Verification Address</Label>
              <Textarea
                rows={3}
                value={editedCase.address}
                onChange={(e) => setEditedCase(prev => ({ ...prev, address: e.target.value }))}
                placeholder="House No., Street, Locality, City, State, PIN"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
